import { alpha, createTheme, Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface: { canvas: string; raised: string; sunken: string; border: string };
  }
  interface PaletteOptions {
    surface?: { canvas: string; raised: string; sunken: string; border: string };
  }
}

/** Brand ramp; the 500 step is the primary action colour. */
const BRAND = {
  50: '#EEF2FF',
  100: '#DDE4FF',
  200: '#BCC9FF',
  300: '#94A9FF',
  400: '#7590FF',
  500: '#5B7FFF',
  600: '#4560E0',
  700: '#3448AF',
  800: '#26357F',
  900: '#1A2557',
};

export const STATUS_COLORS: Record<string, string> = {
  todo: '#5B7FFF',
  in_progress: '#F0A202',
  blocked: '#E2445C',
  done: '#12B76A',
};

export const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#E2445C',
  high: '#F0A202',
  normal: '#5B7FFF',
  low: '#8592A3',
};

export const HEALTH_COLORS: Record<string, string> = {
  on_track: '#12B76A',
  at_risk: '#F0A202',
  off_track: '#E2445C',
};

/** Categorical series colours for charts, ordered for maximum adjacent contrast. */
export const CHART_COLORS = [
  '#5B7FFF',
  '#12B76A',
  '#F0A202',
  '#9C6ADE',
  '#00A9C4',
  '#E2445C',
  '#4C9F70',
  '#FF8A5B',
];

export function buildTheme(mode: 'light' | 'dark'): Theme {
  const isDark = mode === 'dark';

  const surface = isDark
    ? { canvas: '#0B1220', raised: '#131C2E', sunken: '#080E19', border: '#22304A' }
    : { canvas: '#F6F8FC', raised: '#FFFFFF', sunken: '#EEF1F7', border: '#E1E6EF' };

  const theme = createTheme({
    palette: {
      mode,
      primary: { main: BRAND[500], light: BRAND[300], dark: BRAND[700], contrastText: '#FFFFFF' },
      secondary: { main: '#9C6ADE' },
      success: { main: '#12B76A' },
      warning: { main: '#F0A202' },
      error: { main: '#E2445C' },
      info: { main: '#00A9C4' },
      background: { default: surface.canvas, paper: surface.raised },
      divider: surface.border,
      surface,
      text: isDark
        ? { primary: '#E7ECF5', secondary: '#9AA8BF', disabled: '#63718A' }
        : { primary: '#131C2E', secondary: '#5A6880', disabled: '#98A3B5' },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      // Tight, dense scale suited to data-heavy enterprise screens.
      h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.125rem', fontWeight: 650, letterSpacing: '-0.01em' },
      h4: { fontSize: '1rem', fontWeight: 650 },
      h5: { fontSize: '0.9375rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      body1: { fontSize: '0.875rem', lineHeight: 1.55 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.75rem', lineHeight: 1.4 },
      button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none' },
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': { scrollbarWidth: 'thin' },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(isDark ? '#FFFFFF' : '#000000', 0.18),
            borderRadius: 8,
            border: `2px solid ${surface.canvas}`,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            backgroundColor: alpha(isDark ? '#FFFFFF' : '#000000', 0.3),
          },
          body: { backgroundColor: surface.canvas },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: surface.border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0, variant: 'outlined' },
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'border-color 140ms ease, box-shadow 140ms ease',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 14 },
          sizeSmall: { paddingInline: 10, minHeight: 30 },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: '0.75rem' },
          sizeSmall: { height: 22 },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 8, backgroundColor: isDark ? alpha('#000', 0.2) : '#FFF' },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: { fontSize: '0.75rem', borderRadius: 6, paddingBlock: 6 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: surface.border, paddingBlock: 8 },
          head: {
            fontWeight: 650,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            backgroundColor: surface.sunken,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: alpha(BRAND[500], isDark ? 0.22 : 0.1),
              '&:hover': { backgroundColor: alpha(BRAND[500], isDark ? 0.28 : 0.16) },
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, height: 6, backgroundColor: surface.sunken },
          bar: { borderRadius: 999 },
        },
      },
      MuiAvatar: {
        styleOverrides: { root: { fontSize: '0.8125rem', fontWeight: 650 } },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, minHeight: 44 },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 14 } },
      },
    },
  });

  return theme;
}
